import {
  type Paginated,
  type ReportDetail,
  type ReportResolveRequest,
  type ReportsListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own input shape (U3) — never a hand-written copy of the filters. */
type ReportsParams = z.input<typeof ReportsListQuerySchema>;

/**
 * The staff reports queue and its one verdict.
 *
 * ## The verdict returns the report, and the screen still re-reads
 *
 * `resolve` answers the freshly decided row, and writing that straight into the
 * cache would save a round trip. It is deliberately not done, for the reason
 * `trustApi` and `verificationApi` both give about their own rows: rendering
 * from a mutation response gives the screen two sources for one report, and they
 * disagree the moment a colleague acts a second later. Both tags are
 * invalidated instead — the **report**, because its verdict changed, and the
 * **queue**, because a decided report leaves the default filter entirely, which
 * is a different question answered with its own filter and its own ordering.
 *
 * ## It publishes, because a decision can give somebody their visibility back
 *
 * Deciding the last open report against a member lifts the auto-hide
 * (`moderation.autoHideReportThreshold`), so a member who was invisible to the
 * whole platform is visible again. Nothing in the portal subscribes yet — there
 * is no member surface to refresh — but the event is what makes the first one
 * that arrives correct without editing this file (W5).
 */
export const moderationApi = createApi({
  reducerPath: 'moderationApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Reports', 'Report'],
  endpoints: (builder) => ({
    listReports: builder.query<Paginated<ReportDetail>, ReportsParams>({
      query: (params) => ({ url: '/admin/moderation/reports', method: 'GET', params }),
      providesTags: ['Reports'],
    }),

    /**
     * One report, read by the dialog.
     *
     * A separate request rather than the row the queue already holds: the
     * reporter's own words run to 2000 characters and a table cell truncates
     * them, and a dialog reading its own entry cannot be looking at a report the
     * poll has since moved on from.
     */
    report: builder.query<ReportDetail, string>({
      query: (id) => ({ url: `/admin/moderation/reports/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Report' as const, id }],
    }),

    resolveReport: builder.mutation<ReportDetail, { id: string } & ReportResolveRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/moderation/reports/${id}/resolve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Report' as const, id },
        'Reports' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          EVENT_BUS.publish({
            type: 'moderation.report.resolved',
            reportedUserId: data.reportedUserId,
          });
        } catch {
          // Refused — nothing happened, so there is nothing to announce.
          // Swallowed deliberately: `onQueryStarted` has no caller, so a
          // rejection escaping it becomes an unhandled rejection. The failure is
          // already the caller's, through `unwrap()`.
        }
      },
    }),
  }),
});

export const { useListReportsQuery, useReportQuery, useResolveReportMutation } = moderationApi;
